import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DollarSign, CheckCircle2, Clock, RefreshCw, X, Loader2, AlertCircle,
  RotateCcw, Eye, MapPin, Printer, FileCode, Receipt, Ban, Banknote, Percent,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'

// imprime/reimprime a guia GNRE (PDF autenticado via blob)
async function imprimirGnre(id: number) {
  const t = toast.loading('Gerando guia...')
  try {
    const resp = await api.get(`/nfe-proprio/gnre/${id}/pdf`, { responseType: 'blob' })
    const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }))
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
    toast.dismiss(t)
  } catch { toast.dismiss(t); toast.error('Erro ao gerar guia') }
}

interface GnreItem {
  id: number
  carga_id: number | null
  uf_favorecida: string
  receita: string
  valor_principal: string
  valor_fcp: string
  valor_total: string
  qtd_documentos: number
  periodo_referencia: string | null
  data_vencimento: string | null
  numero_controle: string | null
  status: string
  conta_id: number | null
  linha_digitavel: string | null
  data_pagamento: string | null
}

const GNRE_STATUS: Record<string, string> = {
  gerada: 'bg-sky-100 text-sky-800',
  paga: 'bg-emerald-100 text-emerald-800',
  cancelada: 'bg-rose-100 text-rose-700',
}

interface DifalItem {
  id: number
  pedido_id: number | null
  documento_fiscal_id: number | null
  uf_origem: string
  uf_destino: string
  aliquota_interestadual: string | null
  aliquota_interna_destino: string | null
  fcp_percent: string | null
  base_calculo: string
  valor_difal: string
  valor_fcp: string
  total: string
  tipo_destinatario: string
  status_recolhimento: string
  gnre_numero: string | null
  data_vencimento: string | null
  data_recolhimento: string | null
  observacoes: string | null
  created_at: string
}

interface ResumoUF {
  uf: string
  qtd: number
  pendente: string
  recolhido: string
  total: string
}

interface Resumo {
  total_registros: number
  por_uf: ResumoUF[]
  totais: { pendente: string; recolhido: string; total: string }
  total_fcp: string
}

const STATUS_META: Record<string, { cor: string; label: string; Icon: any }> = {
  pendente:  { cor: 'bg-amber-100 text-amber-800',     label: 'Pendente',  Icon: Clock },
  recolhido: { cor: 'bg-emerald-100 text-emerald-800', label: 'Recolhido', Icon: CheckCircle2 },
  vencido:   { cor: 'bg-red-100 text-red-800',         label: 'Vencido',   Icon: AlertCircle },
}


export default function ApuracaoDifalPage() {
  const qc = useQueryClient()
  const [filtroStatus, setFiltroStatus] = useState<string>('')
  const [filtroUF, setFiltroUF] = useState<string>('')
  const [detalhe, setDetalhe] = useState<DifalItem | null>(null)
  const [recolherAlvo, setRecolherAlvo] = useState<DifalItem | null>(null)
  const [gerarGnreOpen, setGerarGnreOpen] = useState(false)
  const [pagarGnreAlvo, setPagarGnreAlvo] = useState<GnreItem | null>(null)
  const [aliquotasOpen, setAliquotasOpen] = useState(false)

  const { data: lista, isLoading } = useQuery<{ total_registros: number; registros: DifalItem[] }>({
    queryKey: ['difal-lista', filtroStatus, filtroUF],
    queryFn: async () => {
      const params: any = {}
      if (filtroStatus) params.status = filtroStatus
      if (filtroUF) params.uf = filtroUF
      return (await api.get('/nfe-proprio/difal-devido', { params })).data
    },
  })

  const { data: resumo } = useQuery<Resumo>({
    queryKey: ['difal-resumo'],
    queryFn: async () => (await api.get('/nfe-proprio/difal-devido/resumo')).data,
  })

  const seedDev = useMutation({
    mutationFn: async () => (await api.post('/nfe-proprio/difal-devido/seed-dev')).data,
    onSuccess: (d) => {
      toast.success(`${d.criados} registros ficticios criados`)
      qc.invalidateQueries({ queryKey: ['difal-lista'] })
      qc.invalidateQueries({ queryKey: ['difal-resumo'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro no seed'),
  })

  const reabrir = useMutation({
    mutationFn: async (id: number) => (await api.post(`/nfe-proprio/difal-devido/${id}/reabrir`)).data,
    onSuccess: () => {
      toast.success('DIFAL reaberto')
      qc.invalidateQueries({ queryKey: ['difal-lista'] })
      qc.invalidateQueries({ queryKey: ['difal-resumo'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao reabrir'),
  })

  // ---- GNRE ----
  const { data: gnreData } = useQuery<{ total: number; registros: GnreItem[] }>({
    queryKey: ['gnre-lista'],
    queryFn: async () => (await api.get('/nfe-proprio/gnre')).data,
  })
  const invalidarTudo = () => {
    qc.invalidateQueries({ queryKey: ['difal-lista'] })
    qc.invalidateQueries({ queryKey: ['difal-resumo'] })
    qc.invalidateQueries({ queryKey: ['gnre-lista'] })
  }
  const cancelarGnre = useMutation({
    mutationFn: async (id: number) => (await api.post(`/nfe-proprio/gnre/${id}/cancelar`)).data,
    onSuccess: () => { toast.success('GNRE cancelada'); invalidarTudo() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao cancelar'),
  })

  const registros = lista?.registros || []
  const gnres = gnreData?.registros || []

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <DollarSign className="text-brand-600" /> Apuração DIFAL
        </h1>
        <p className="text-ink-500 text-sm mt-1">
          Gerencia o DIFAL (ICMS Diferencial de Alíquota) devido em operações interestaduais.
          Marque como recolhido após emitir a GNRE e pagar.
        </p>
      </div>

      {/* Cards de totais */}
      {resumo && resumo.total_registros > 0 && (
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white border border-ink-200 rounded-lg p-4">
            <div className="text-xs text-ink-500 uppercase">Total DIFAL</div>
            <div className="text-2xl font-bold text-ink-900">{formatMoney(Number(resumo.totais.total))}</div>
            <div className="text-xs text-ink-400">{resumo.total_registros} registro(s)</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-xs text-amber-700 uppercase">Pendente</div>
            <div className="text-2xl font-bold text-amber-900">{formatMoney(Number(resumo.totais.pendente))}</div>
            <div className="text-xs text-amber-600">A recolher</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="text-xs text-emerald-700 uppercase">Recolhido</div>
            <div className="text-2xl font-bold text-emerald-900">{formatMoney(Number(resumo.totais.recolhido))}</div>
            <div className="text-xs text-emerald-600">Ja pago</div>
          </div>
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
            <div className="text-xs text-violet-700 uppercase">Total FCP</div>
            <div className="text-2xl font-bold text-violet-900">{formatMoney(Number(resumo.total_fcp))}</div>
            <div className="text-xs text-violet-600">Fundo combate pobreza</div>
          </div>
        </div>
      )}

      {/* Resumo por UF */}
      {resumo && resumo.por_uf.length > 0 && (
        <div className="bg-white border border-ink-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-1">
            <MapPin size={14} /> Resumo por UF Destino
          </h3>
          <div className="grid grid-cols-5 gap-2">
            {resumo.por_uf.map((u) => (
              <button
                key={u.uf}
                onClick={() => setFiltroUF(filtroUF === u.uf ? '' : u.uf)}
                className={`text-left p-2 rounded border text-xs ${
                  filtroUF === u.uf
                    ? 'bg-brand-50 border-brand-300'
                    : 'bg-ink-50 border-ink-200 hover:bg-ink-100'
                }`}
              >
                <div className="font-bold text-sm">{u.uf}</div>
                <div className="text-ink-500">{u.qtd} NFe(s)</div>
                <div className="font-medium mt-1">{formatMoney(Number(u.total))}</div>
                {Number(u.pendente) > 0 && (
                  <div className="text-amber-700">Pend: {formatMoney(Number(u.pendente))}</div>
                )}
                {Number(u.recolhido) > 0 && (
                  <div className="text-emerald-700">OK: {formatMoney(Number(u.recolhido))}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filtros + acoes */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-600">Status:</span>
          {['', 'pendente', 'recolhido'].map((s) => (
            <button
              key={s || 'todos'}
              onClick={() => setFiltroStatus(s)}
              className={`px-3 py-1 rounded text-sm ${
                filtroStatus === s ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
              }`}
            >
              {s || 'todos'}
            </button>
          ))}
          {filtroUF && (
            <button
              onClick={() => setFiltroUF('')}
              className="px-3 py-1 rounded text-sm bg-amber-100 text-amber-800 hover:bg-amber-200 flex items-center gap-1"
            >
              UF: {filtroUF} <X size={12} />
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setAliquotasOpen(true)}
            className="btn-ghost text-xs"
            title="Cadastrar/editar alíquota interna ICMS + FCP por UF"
          >
            <Percent size={13} /> Alíquotas UF
          </button>
          <button
            onClick={() => setGerarGnreOpen(true)}
            className="btn-primary text-xs"
            title="Gera a GNRE agrupando o DIFAL pendente por UF (lança em contas a pagar)"
          >
            <Receipt size={13} /> Gerar GNRE {filtroUF ? `(${filtroUF})` : ''}
          </button>
          <button
            onClick={() => seedDev.mutate()}
            disabled={seedDev.isPending}
            className="btn-ghost text-xs"
            title="Cria 5 registros ficticios (apenas em homologacao)"
          >
            {seedDev.isPending ? <Loader2 className="animate-spin" size={12} /> : '🧪'} Seed dev
          </button>
          <button
            onClick={() => {
              qc.invalidateQueries({ queryKey: ['difal-lista'] })
              qc.invalidateQueries({ queryKey: ['difal-resumo'] })
            }}
            className="btn-ghost text-xs"
          >
            <RefreshCw size={12} /> Atualizar
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50">
            <tr>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">ID</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Pedido</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Origem → Destino</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Base</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">DIFAL</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">FCP</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Total</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Status</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">GNRE</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Vencimento</th>
              <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && (
              <tr><td colSpan={11} className="text-center py-6 text-ink-400">Carregando...</td></tr>
            )}
            {!isLoading && registros.length === 0 && (
              <tr>
                <td colSpan={11} className="text-center py-10">
                  <AlertCircle className="mx-auto mb-2 text-ink-300" size={32} />
                  <p className="text-ink-500">Nenhum DIFAL devido.</p>
                  <p className="text-ink-400 text-xs mt-1">
                    Registros aparecem aqui automaticamente ao emitir NFe interestadual.
                    Em homologação, use "Seed dev" pra popular dados de teste.
                  </p>
                </td>
              </tr>
            )}
            {registros.map((r) => {
              const st = STATUS_META[r.status_recolhimento] || { cor: 'bg-ink-100', label: r.status_recolhimento, Icon: DollarSign }
              return (
                <tr key={r.id}
                  onClick={() => setDetalhe(r)}
                  className="hover:bg-violet-50 cursor-pointer">
                  <td className="px-3 py-2.5 font-mono text-xs">{r.id}</td>
                  <td className="px-3 py-2.5 text-xs">
                    {r.pedido_id ? `#${r.pedido_id}` : <span className="text-ink-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs font-medium">
                    {r.uf_origem} → <strong>{r.uf_destino}</strong>
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs">{formatMoney(Number(r.base_calculo))}</td>
                  <td className="px-3 py-2.5 text-right text-xs">{formatMoney(Number(r.valor_difal))}</td>
                  <td className="px-3 py-2.5 text-right text-xs text-violet-700">
                    {Number(r.valor_fcp) > 0 ? formatMoney(Number(r.valor_fcp)) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right text-sm font-semibold">{formatMoney(Number(r.total))}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${st.cor}`}>
                      <st.Icon size={12} /> {st.label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs font-mono text-ink-600">{r.gnre_numero || '—'}</td>
                  <td className="px-3 py-2.5 text-center text-xs text-ink-500">
                    {r.data_vencimento ? new Date(r.data_vencimento).toLocaleDateString('pt-BR') : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right" onClick={ev => ev.stopPropagation()}>
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => setDetalhe(r)}
                        className="text-ink-500 hover:text-brand-600 p-1"
                        title="Detalhes"
                      >
                        <Eye size={14} />
                      </button>
                      {r.status_recolhimento === 'pendente' && (
                        <button
                          onClick={() => setRecolherAlvo(r)}
                          className="text-emerald-600 hover:text-emerald-800 p-1"
                          title="Marcar como recolhido"
                        >
                          <CheckCircle2 size={14} />
                        </button>
                      )}
                      {r.status_recolhimento === 'recolhido' && (
                        <button
                          onClick={() => {
                            if (confirm('Reabrir este DIFAL? Voltará pra pendente.')) reabrir.mutate(r.id)
                          }}
                          className="text-amber-600 hover:text-amber-800 p-1"
                          title="Reabrir (desmarcar recolhido)"
                        >
                          <RotateCcw size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ---- Guias GNRE ---- */}
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
          <Receipt size={18} className="text-brand-600" /> Guias GNRE
          <span className="text-xs font-normal text-ink-400">({gnres.length})</span>
        </h2>
        <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50">
              <tr>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Controle</th>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">UF</th>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Receita</th>
                <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Principal</th>
                <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">FCP</th>
                <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Total</th>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Venc.</th>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Status</th>
                <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {gnres.length === 0 && (
                <tr><td colSpan={9} className="text-center py-8 text-ink-400">
                  Nenhuma guia gerada. Use "Gerar GNRE" pra agrupar o DIFAL pendente por UF.
                </td></tr>
              )}
              {gnres.map((g) => (
                <tr key={g.id} className="hover:bg-violet-50">
                  <td className="px-3 py-2.5 font-mono text-xs">{g.numero_controle}</td>
                  <td className="px-3 py-2.5 text-center font-bold text-xs">{g.uf_favorecida}</td>
                  <td className="px-3 py-2.5 text-xs text-ink-500">{g.receita}</td>
                  <td className="px-3 py-2.5 text-right text-xs">{formatMoney(Number(g.valor_principal))}</td>
                  <td className="px-3 py-2.5 text-right text-xs text-violet-700">
                    {Number(g.valor_fcp) > 0 ? formatMoney(Number(g.valor_fcp)) : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right text-sm font-semibold">{formatMoney(Number(g.valor_total))}</td>
                  <td className="px-3 py-2.5 text-center text-xs text-ink-500">
                    {g.data_vencimento ? new Date(g.data_vencimento).toLocaleDateString('pt-BR') : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded ${GNRE_STATUS[g.status] || 'bg-ink-100'}`}>{g.status}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => imprimirGnre(g.id)} className="text-brand-600 hover:text-brand-800 p-1"
                        title="Imprimir / Reimprimir guia (PDF)"><Printer size={14} /></button>
                      <a href={`/api/nfe-proprio/gnre/${g.id}/xml`} className="text-ink-500 hover:text-ink-800 p-1"
                        title="Baixar XML do lote" onClick={(e) => { e.preventDefault(); api.get(`/nfe-proprio/gnre/${g.id}/xml`, { responseType: 'blob' }).then(r => { const u = URL.createObjectURL(new Blob([r.data], { type: 'application/xml' })); const a = document.createElement('a'); a.href = u; a.download = `gnre-${g.numero_controle}.xml`; a.click() }) }}>
                        <FileCode size={14} /></a>
                      {g.status === 'gerada' && (
                        <>
                          <button onClick={() => setPagarGnreAlvo(g)} className="text-emerald-600 hover:text-emerald-800 p-1"
                            title="Marcar paga (baixa conta + DIFAL)"><Banknote size={14} /></button>
                          <button onClick={() => { if (confirm('Cancelar esta GNRE? A conta a pagar será cancelada e o DIFAL volta a pendente.')) cancelarGnre.mutate(g.id) }}
                            className="text-rose-600 hover:text-rose-800 p-1" title="Cancelar"><Ban size={14} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detalhe && <DetalheModal item={detalhe} onClose={() => setDetalhe(null)} />}
      {recolherAlvo && (
        <RecolherModal
          item={recolherAlvo}
          onClose={() => setRecolherAlvo(null)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['difal-lista'] })
            qc.invalidateQueries({ queryKey: ['difal-resumo'] })
            setRecolherAlvo(null)
          }}
        />
      )}
      {gerarGnreOpen && (
        <GerarGnreModal
          uf={filtroUF}
          onClose={() => setGerarGnreOpen(false)}
          onSuccess={() => { invalidarTudo(); setGerarGnreOpen(false) }}
        />
      )}
      {pagarGnreAlvo && (
        <PagarGnreModal
          item={pagarGnreAlvo}
          onClose={() => setPagarGnreAlvo(null)}
          onSuccess={() => { invalidarTudo(); setPagarGnreAlvo(null) }}
        />
      )}
      {aliquotasOpen && <AliquotasModal onClose={() => setAliquotasOpen(false)} />}
    </div>
  )
}


// ============================================================
// Modal: alíquotas internas ICMS + FCP por UF
// ============================================================
interface AliquotaUFItem { uf: string; nome: string; aliquota_interna: string; fcp_percent: string; regiao: string }

function AliquotasModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const { data: lista = [], isLoading } = useQuery<AliquotaUFItem[]>({
    queryKey: ['aliquotas-uf'],
    queryFn: async () => (await api.get('/nfe-proprio/aliquotas')).data,
  })
  const [edits, setEdits] = useState<Record<string, { interna: string; fcp: string }>>({})
  const setEdit = (uf: string, k: 'interna' | 'fcp', v: string, cur: AliquotaUFItem) =>
    setEdits((p) => ({
      ...p,
      [uf]: {
        interna: k === 'interna' ? v : (p[uf]?.interna ?? cur.aliquota_interna),
        fcp: k === 'fcp' ? v : (p[uf]?.fcp ?? cur.fcp_percent),
      },
    }))

  const salvar = useMutation({
    mutationFn: async ({ uf, interna, fcp }: { uf: string; interna: string; fcp: string }) =>
      (await api.put(`/nfe-proprio/aliquotas/${uf}`, { aliquota_interna: interna, fcp_percent: fcp })).data,
    onSuccess: (_d, v) => {
      toast.success(`${v.uf} salvo`)
      setEdits((p) => { const n = { ...p }; delete n[v.uf]; return n })
      qc.invalidateQueries({ queryKey: ['aliquotas-uf'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })
  const seed = useMutation({
    mutationFn: async () => (await api.post('/nfe-proprio/aliquotas/seed')).data,
    onSuccess: (d: any) => { toast.success(`${d.criados} UF(s) criada(s), ${d.pulados} já existiam`); qc.invalidateQueries({ queryKey: ['aliquotas-uf'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro no seed'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[88vh] flex flex-col">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Percent className="text-brand-600" size={18} /> Alíquotas internas ICMS por UF
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="px-4 py-2 flex items-center justify-between bg-amber-50 border-b border-amber-100">
          <span className="text-xs text-amber-700">
            Valores são a <strong>alíquota modal padrão 2024/2025</strong>. FCP é adicional por produto — ajuste conforme seu caso.
          </span>
          <button onClick={() => seed.mutate()} disabled={seed.isPending} className="btn-ghost text-xs whitespace-nowrap">
            {seed.isPending ? <Loader2 className="animate-spin" size={12} /> : 'Preencher padrão'}
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 sticky top-0">
              <tr>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">UF</th>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Estado</th>
                <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2 w-28">Interna %</th>
                <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2 w-28">FCP %</th>
                <th className="px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {isLoading && <tr><td colSpan={5} className="text-center py-6 text-ink-400">Carregando...</td></tr>}
              {lista.map((a) => {
                const e = edits[a.uf]
                const interna = e?.interna ?? a.aliquota_interna
                const fcp = e?.fcp ?? a.fcp_percent
                const dirty = !!e && (e.interna !== a.aliquota_interna || e.fcp !== a.fcp_percent)
                return (
                  <tr key={a.uf} className={dirty ? 'bg-amber-50' : ''}>
                    <td className="px-3 py-1.5 font-bold text-xs">{a.uf}</td>
                    <td className="px-3 py-1.5 text-xs text-ink-500">{a.nome}</td>
                    <td className="px-3 py-1.5 text-right">
                      <input className="input !py-1 !px-2 text-right w-20" value={interna}
                        onChange={(ev) => setEdit(a.uf, 'interna', ev.target.value, a)} />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input className="input !py-1 !px-2 text-right w-20" value={fcp}
                        onChange={(ev) => setEdit(a.uf, 'fcp', ev.target.value, a)} />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {dirty && (
                        <button onClick={() => salvar.mutate({ uf: a.uf, interna, fcp })}
                          disabled={salvar.isPending}
                          className="btn-primary !py-1 !px-2 text-xs">Salvar</button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="border-t border-ink-200 p-3 flex justify-end">
          <button className="btn-ghost" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  )
}


// ============================================================
// Modal: gerar GNRE
// ============================================================
function GerarGnreModal({ uf, onClose, onSuccess }: { uf: string; onClose: () => void; onSuccess: () => void }) {
  const [venc, setVenc] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 15)
    return d.toISOString().slice(0, 10)
  })
  const mut = useMutation({
    mutationFn: async () => (await api.post('/nfe-proprio/gnre/gerar', {
      uf: uf || null, data_vencimento: venc,
    })).data,
    onSuccess: (d: any) => { toast.success(`${d.geradas} guia(s) gerada(s)`); onSuccess() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao gerar GNRE'),
  })
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Receipt className="text-brand-600" size={18} /> Gerar GNRE
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <p className="text-ink-500 text-xs">
            Agrupa o DIFAL <strong>pendente</strong> {uf ? <>da UF <strong>{uf}</strong></> : 'por UF favorecida'} em
            guias e lança cada uma em <strong>contas a pagar</strong>.
          </p>
          <div>
            <label className="label">Data de vencimento</label>
            <input type="date" className="input" value={venc} onChange={(e) => setVenc(e.target.value)} />
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost flex-1" onClick={onClose}>Voltar</button>
            <button className="btn-primary flex-1" disabled={mut.isPending} onClick={() => mut.mutate()}>
              {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : 'Gerar guias'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}


// ============================================================
// Modal: pagar GNRE
// ============================================================
function PagarGnreModal({ item, onClose, onSuccess }: { item: GnreItem; onClose: () => void; onSuccess: () => void }) {
  const [linha, setLinha] = useState('')
  const [data, setData] = useState(new Date().toISOString().slice(0, 10))
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/nfe-proprio/gnre/${item.id}/pagar`, {
      linha_digitavel: linha.trim() || null,
      codigo_barras: linha.replace(/\D/g, '') || null,
      data_pagamento: data,
    })).data,
    onSuccess: () => { toast.success('GNRE paga — conta e DIFAL baixados'); onSuccess() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao pagar'),
  })
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Banknote className="text-emerald-600" size={18} /> Pagar GNRE
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="bg-ink-50 p-2 rounded text-xs">
            <div>Controle {item.numero_controle} · {item.uf_favorecida}</div>
            <div className="font-semibold">{formatMoney(Number(item.valor_total))}</div>
          </div>
          <div>
            <label className="label">Linha digitável / código de barras</label>
            <input className="input font-mono" value={linha} onChange={(e) => setLinha(e.target.value)}
              placeholder="Cole a linha digitável da guia paga" />
          </div>
          <div>
            <label className="label">Data do pagamento</label>
            <input type="date" className="input" value={data} onChange={(e) => setData(e.target.value)} />
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost flex-1" onClick={onClose}>Voltar</button>
            <button className="btn-primary flex-1 bg-emerald-600 hover:bg-emerald-700 border-emerald-600"
              disabled={mut.isPending} onClick={() => mut.mutate()}>
              {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : 'Confirmar pagamento'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}


// ============================================================
// Modal de detalhe
// ============================================================
function DetalheModal({ item, onClose }: { item: DifalItem; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">DIFAL #{item.id}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Pedido" value={item.pedido_id ? `#${item.pedido_id}` : '—'} />
            <Info label="Status" value={item.status_recolhimento} />
            <Info label="UF Origem" value={item.uf_origem} />
            <Info label="UF Destino" value={item.uf_destino} />
            <Info label="Tipo destinatário" value={item.tipo_destinatario || '—'} />
            <Info label="Aliq. interestadual" value={item.aliquota_interestadual ? `${item.aliquota_interestadual}%` : '—'} />
            <Info label="Aliq. interna destino" value={item.aliquota_interna_destino ? `${item.aliquota_interna_destino}%` : '—'} />
            <Info label="% FCP" value={item.fcp_percent ? `${item.fcp_percent}%` : '—'} />
          </div>
          <div className="border-t border-ink-100 pt-3 grid grid-cols-2 gap-3">
            <Info label="Base de cálculo" value={formatMoney(Number(item.base_calculo))} />
            <Info label="Valor DIFAL" value={formatMoney(Number(item.valor_difal))} />
            <Info label="Valor FCP" value={formatMoney(Number(item.valor_fcp))} />
            <Info label="TOTAL" value={formatMoney(Number(item.total))} highlight />
          </div>
          <div className="border-t border-ink-100 pt-3 grid grid-cols-2 gap-3">
            <Info label="GNRE" value={item.gnre_numero || '—'} />
            <Info label="Data vencimento" value={item.data_vencimento ? new Date(item.data_vencimento).toLocaleDateString('pt-BR') : '—'} />
            <Info label="Data recolhimento" value={item.data_recolhimento ? new Date(item.data_recolhimento).toLocaleDateString('pt-BR') : '—'} />
            <Info label="Criado em" value={new Date(item.created_at).toLocaleString('pt-BR')} />
          </div>
          {item.observacoes && (
            <div>
              <div className="text-xs text-ink-500 uppercase font-semibold">Observações</div>
              <pre className="text-xs whitespace-pre-wrap bg-ink-50 p-2 rounded mt-1">{item.observacoes}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


function Info({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <div className="text-xs text-ink-500 uppercase font-semibold">{label}</div>
      <div className={`text-sm ${highlight ? 'font-bold text-brand-700' : ''}`}>{value}</div>
    </div>
  )
}


// ============================================================
// Modal de recolhimento
// ============================================================
function RecolherModal({
  item, onClose, onSuccess,
}: { item: DifalItem; onClose: () => void; onSuccess: () => void }) {
  const [gnre, setGnre] = useState('')
  const [data, setData] = useState(new Date().toISOString().slice(0, 10))
  const [obs, setObs] = useState('')

  const mut = useMutation({
    mutationFn: async () =>
      (await api.post(`/nfe-proprio/difal-devido/${item.id}/recolher`, {
        gnre_numero: gnre.trim() || null,
        data_recolhimento: data,
        observacoes: obs.trim() || null,
      })).data,
    onSuccess: () => {
      toast.success('DIFAL marcado como recolhido')
      onSuccess()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CheckCircle2 className="text-emerald-600" size={18} /> Marcar recolhido
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-ink-50 p-2 rounded text-xs">
            <div>DIFAL #{item.id} · {item.uf_origem} → {item.uf_destino}</div>
            <div className="font-semibold">{formatMoney(Number(item.total))}</div>
          </div>
          <div>
            <label className="label">Número da GNRE</label>
            <input className="input" value={gnre} onChange={(e) => setGnre(e.target.value)} placeholder="Ex: 2026001234" />
          </div>
          <div>
            <label className="label">Data do recolhimento</label>
            <input type="date" className="input" value={data} onChange={(e) => setData(e.target.value)} />
          </div>
          <div>
            <label className="label">Observações</label>
            <textarea className="input" rows={2} value={obs} onChange={(e) => setObs(e.target.value)}
              placeholder="Pago via banco X, comprovante arquivado em..." />
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost flex-1" onClick={onClose}>Voltar</button>
            <button
              className="btn-primary flex-1 bg-emerald-600 hover:bg-emerald-700 border-emerald-600"
              disabled={mut.isPending}
              onClick={() => mut.mutate()}
            >
              {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : 'Confirmar recolhimento'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
